import type { BuildingDef } from "../schema/building.js";
import type { Resource } from "../schema/common.js";

/**
 * Command Nexus upgrade time in seconds, indexed by the target level (20-base.md R12).
 * Index 0 and 1 are unused (level 1 is pre-built).
 */
export const HQ_TIME_SEC: readonly number[] = [
  0, 0, 10, 30, 120, 300, 720, 1500, 2700, 4500, 7200, 10800, 16200, 23400, 32400, 43200, 57600, 79200, 108000,
  144000, 187200, 244800, 316800, 410400, 518400, 648000, 820800, 1036800, 1296000, 1641600, 2073600,
];

export function hqTimeSec(level: number): number {
  const v = HQ_TIME_SEC[level];
  if (v === undefined) throw new RangeError(`no HQ time for level ${level}`);
  return v;
}

export function buildingTimeSec(def: BuildingDef, level: number): number {
  return Math.max(5, Math.round(hqTimeSec(level) * def.timeWeight));
}

/** Tuned so an HQ upgrade costs roughly 4–12 hours of full production at that level (20-base.md R12). */
const COST_BASE = { RAT: 900, POL: 850, ALY: 400, ENC: 150 } as const;
const COST_GROWTH = 1.33;

function roundTo(n: number, step: number): number {
  return Math.round(n / step) * step;
}

/** Resource cost to upgrade a building to `level` (from level−1); ALY from level 3, ENC from level 8. */
export function buildingCost(def: BuildingDef, level: number): Record<"RAT" | "POL" | "ALY" | "ENC", number> {
  const scale = def.costWeight * Math.pow(COST_GROWTH, level - 1);
  const step = level >= 20 ? 1000 : level >= 10 ? 100 : 10;
  return {
    RAT: roundTo(COST_BASE.RAT * scale, step),
    POL: roundTo(COST_BASE.POL * scale, step),
    ALY: level >= 3 ? roundTo(COST_BASE.ALY * scale, step) : 0,
    ENC: level >= 8 ? roundTo(COST_BASE.ENC * scale, step) : 0,
  };
}

const PRODUCTION_BASE: Record<Resource, number> = { RAT: 320, POL: 300, ALY: 180, ENC: 60, SCR: 90 };
const PRODUCTION_GROWTH = 1.17;

export function productionPerHour(resource: Resource, level: number): number {
  return Math.round(PRODUCTION_BASE[resource] * Math.pow(PRODUCTION_GROWTH, level - 1));
}

export function storageCapacity(level: number): number {
  return Math.round(40_000 * Math.pow(1.21, level - 1));
}

/** Protected share of storage capacity that raids cannot take (base 25 %). */
export const PROTECTED_SHARE = 0.25;

/**
 * Power draw per building level (D126). It was 4 × level, which grew faster than a mid-game enclave can produce: every
 * account from HQ 7 on ran a permanent deficit (−25 % production) with no way out. 2.5 × level keeps the reactor a
 * real duty — let it fall three levels behind and the deficit is back — without making it impossible.
 */
export function powerConsumption(level: number): number {
  return Math.ceil(2.5 * level);
}

export function powerProduction(level: number): number {
  return Math.round(120 * Math.pow(1.16, level - 1));
}

export function turretStats(level: number): { hp: number; atk: number } {
  return { hp: Math.round(1500 * Math.pow(1.2, level - 1)), atk: Math.round(120 * Math.pow(1.18, level - 1)) };
}

export function xpForLevel(level: number): number {
  return Math.round(100 * Math.pow(level, 1.8));
}

/** Querschnitt: required average building level to upgrade the HQ from `level` to `level + 1`. */
export function hqRequiredAverage(level: number): number {
  return Math.max(0, level - 1 - Math.floor(level / 10));
}

export const STARTER_BALANCES = { RAT: 5000, POL: 5000, ALY: 1000, ENC: 0, SCR: 500, credits: 0, stamina: 100 } as const;
